import React from "react";
import { Link, useLocation } from "react-router-dom";
import SupportSEO from "../../components/Support/SupportSEO";
import SupportBreadcrumb from "../../components/Support/SupportBreadcrumb";
import MarkdownRenderer from "../../components/Support/MarkdownRenderer";
import contentManagerV3 from "../../utils/contentManagerV3";
import { generateSupportBreadcrumbs } from "../../utils/supportBreadcrumbs";
import { faqPairsFromMarkdown } from "../../components/SEO/faqPageJsonLd";

const compactLink =
  "inline-flex items-center justify-center px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink";

/**
 * Dynamic Article Viewer
 * Displays any article from the content management system based on the URL path
 */

const ArticleViewer = () => {
  const { pathname: location } = useLocation();

  // Find the article by the full path
  const article = contentManagerV3.getArticleByPath(location);

  if (!article) {
    return (
      <div className="min-h-screen bg-brand-cream py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-board p-8 text-center">
            <h1 className="heading-display text-xl mb-2">Article not found</h1>
            <p className="text-brand-muted mb-6">
              That help article is missing or the link is old.
            </p>
            <Link to="/support" className={compactLink}>
              Back to Help
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render FAQ content
  if (article.type === "faq") {
    return <FAQArticleViewer article={article} />;
  }

  // Render tutorial content
  if (article.type === "tutorial") {
    return <TutorialArticleViewer article={article} />;
  }

  // Default article viewer
  return <DefaultArticleViewer article={article} />;
};

const ArticleShell = ({ article, children, meta }) => {
  const breadcrumbs = generateSupportBreadcrumbs(article.path, article);
  const seo = article.seo && typeof article.seo === "object" ? article.seo : null;
  const keywords = Array.isArray(seo?.keywords)
    ? seo.keywords
    : Array.isArray(article.tags)
      ? article.tags
      : [];

  return (
    <>
      <SupportSEO
        title={seo?.title || article.title}
        description={seo?.description || article.description}
        keywords={keywords}
        canonicalUrl={article.path}
        faq={
          article.type === "faq"
            ? faqPairsFromMarkdown(article.content?.markdown)
            : []
        }
      />

      <div className="min-h-screen bg-brand-cream py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-4">
            <SupportBreadcrumb items={breadcrumbs} />
          </div>

          <div className="card-board overflow-hidden mb-6">
            <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
              <h1 className="font-display font-bold text-xl md:text-2xl text-brand-cream tracking-tight">
                {article.title || "Article"}
              </h1>
              {article.description ? (
                <p className="text-sm text-brand-mist mt-1">
                  {article.description}
                </p>
              ) : null}
              {meta}
            </div>
            <div className="p-6 md:p-8">{children}</div>
          </div>
        </div>
      </div>
    </>
  );
};

const HelpFooter = () => (
  <div className="mt-8 pt-6 border-t border-brand-charcoal/10">
    <h3 className="heading-display text-lg mb-2">Need more help?</h3>
    <p className="text-brand-muted mb-4">
      Related guides, and a ticket if you're still stuck.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-display font-bold text-brand-charcoal mb-2">
          Related tutorials
        </h4>
        <ul className="space-y-1 text-sm">
          <li>
            <Link
              to="/support/tutorials/getting-started"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              Getting started
            </Link>
          </li>
          <li>
            <Link
              to="/support/tutorials/create-tournament"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              How to create a bowling tournament
            </Link>
          </li>
          <li>
            <Link
              to="/support/tutorials/run-a-tournament"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              How to run a bowling tournament
            </Link>
          </li>
          <li>
            <Link
              to="/support/tutorials/run-brackets"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              How to run bowling brackets
            </Link>
          </li>
        </ul>
      </div>
      <div>
        <h4 className="font-display font-bold text-brand-charcoal mb-2">
          Quick help
        </h4>
        <ul className="space-y-1 text-sm">
          <li>
            <Link
              to="/support/faqs/account-questions"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              Frequently Asked Questions
            </Link>
          </li>
          <li>
            <Link
              to="/support/contact"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              Contact Support
            </Link>
          </li>
          <li>
            <Link
              to="/support"
              className="text-brand-gold-ink underline hover:text-brand-charcoal"
            >
              Help home
            </Link>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const FAQArticleViewer = ({ article }) => {
  return (
    <ArticleShell article={article}>
      <MarkdownRenderer content={article.content.markdown} />
      <HelpFooter />
    </ArticleShell>
  );
};

const TutorialArticleViewer = ({ article }) => {
  return (
    <ArticleShell
      article={article}
      meta={
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-2 text-sm text-brand-mist">
          <span>{article.estimatedTime || "N/A"}</span>
          <span>Updated {article.lastUpdated || "N/A"}</span>
        </div>
      }
    >
      <MarkdownRenderer content={article.content.markdown} />
      <HelpFooter />
    </ArticleShell>
  );
};

const DefaultArticleViewer = ({ article }) => {
  return (
    <ArticleShell article={article}>
      <p className="text-brand-muted">{article.description}</p>
    </ArticleShell>
  );
};

export default ArticleViewer;
